import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { getModalSwitch } from '@app/store/actions';
import { mainMenuTypes, IMenuItem, mainMenuTop } from '@app/components/Sidebar/menuObject';
import { getMenuItemById, hasPermission } from '@app/helpers';
import { getAppsRequest, toggleNotification } from '@app/store/actions/launchpad.actions';
import { ILaunchPadApp } from '@app/models/launchpadApp.model';
import { IFabMenu, IListPageBarEnabled, OrderType, Order } from '@app/models';
import LaunchPadApps from './components/apps/list/LaunchPadAppsList.component';
import { Alert, NoContent, FabMenu, PageLoader, IModalData, ModalComponent } from '@app/components';
import AddNewApplicationToLibrary from '@app/pages/launchPad/dialogs/addNewApplicationToLibrary';
import { PasslyAlert } from '@app/componentsV2';
import { Box } from '@material-ui/core';
import { Administrator } from '@app/store/constants';
import PasslyEolBanner from './components/PasslyEolBanner.component';

const LaunchPadPage = () => {
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);

    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Launchpad, mainMenuTop);

    const [order] = useState<OrderType>(Order.asc);
    const [orderBy] = useState('LastLaunched');

    const apps = useSelector((state: any) => state.launchPad.apps);
    const myCatalogApps = useSelector((state: any) => state.launchPad.catalogApps);
    const [showNotification, setShowNotification] = useState<boolean>(true);
    const appsLoading = useSelector((state: any) => state.launchPad.appsLoading);

    const dispatch = useDispatch();

    const deadline = new Date(2024, 0, 1);
    const today = new Date();
    const showAuthAnvilEOL = today < deadline;

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: false,
        launchpadSortingEnabled: true
    };

    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    useSetPage(setListPageBarEnabledOptions, item);

    useEffect(() => {
        dispatch(toggleNotification(false));
        dispatch(getAppsRequest(orderBy, order));
    }, []);

    const addApp = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('NEW_APP'),
            modalSwitcher: modalSwitchState,
            size: 'lg',
            form: <AddNewApplicationToLibrary handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            dispatch(getAppsRequest(orderBy, order));
        }
    };

    const isProvisioning = (apps: ILaunchPadApp[]) => {
        let appsRequiringProvisioning: string[] = [];
        apps.forEach(function (app) {
            if (!app.UserConfiguration || !app.UserConfiguration.Provisioned) {
                if (app.RequiresProvisioning) {
                    appsRequiringProvisioning.push(app.Id);
                }
            } else {
                app.RequiresProvisioning = false;
            }
        });

        apps.forEach(function (app) {
            if (appsRequiringProvisioning.indexOf(app.Id) > -1) {
                app.RequiresProvisioning = true;
            }
        });

        return appsRequiringProvisioning.length > 0;
    };

    const closeProvisioningMessage = () => {
        setShowNotification(false);
    };

    const fabMenu: IFabMenu = {
        name: 'ADD_APP',
        actions: [
            {
                name: 'ADD_APP',
                method: () => addApp(),
                iconElement: <i className='icon icon-library' />
            }
        ]
    };

    return (
        <>
            <PasslyEolBanner/>
            {hasPermission([Administrator]) && showAuthAnvilEOL && (
                <Box mb={2}>
                    <PasslyAlert severity={'info'} elevation={6} variant={'outlined'}>
                        <div dangerouslySetInnerHTML={{ __html: translation('AUTH_ANVIL_EOL') }} />
                    </PasslyAlert>
                </Box>
            )}
            <ModalComponent {...modalData} />
            {appsLoading ? (
                <PageLoader />
            ) : (
                <main className='content'>
                    {isProvisioning(apps) && showNotification && (
                        <Alert
                            type={'default'}
                            icon={'notification:sync'}
                            header={translation('APP_REQUIRES_PROVISIONING')}
                            description={translation('APP_REQUIRES_PROVISIONING_BODY')}
                            onClick={() => closeProvisioningMessage()}
                        />
                    )}

                    {apps.length > 0 && <LaunchPadApps apps={apps} handleRefreshPage={handlePageRefreshFromDialog} />}

                    {apps.length === 0 && <NoContent header={'READY_FOR_SINGLE_SIGN_ON'} text={'READY_FOR_SINGLE_SIGN_ON_BODY'} />}
                    {myCatalogApps.length !== 0 && <FabMenu menu={fabMenu} />}
                </main>
            )}
        </>
    );
};

export default LaunchPadPage;
